/**
 * The dry run: what `./pacs-cli generate` would change, and whether it is safe.
 *
 * Three things this page refuses to do, each because the opposite is how an
 * operator gets misled into approving a bad change.
 *
 * It never renders a skipped gate as a passing one. `skipped` gets its own
 * glyph, its own tone and the word "not run"; a checklist that quietly omits
 * what it could not verify reads as approval.
 *
 * It separates cosmetic churn from real changes. Rendering is deterministic, so
 * an unchanged fleet diffs to nothing — but a file already on disk from the
 * legacy generator can differ by a re-salted bcrypt hash alone. Those are
 * labelled, collapsed and excluded from the headline count.
 *
 * And it states, in words, that applying is not available here. A missing
 * button is ambiguous: not built, not permitted, or not safe right now all look
 * identical.
 *
 * ── the redesign ────────────────────────────────────────────────────────────
 *
 * Carbon is gone and the page is now a staged rollout view rather than a wall
 * of diff text, but every refusal above survived the move — the gate checklist
 * is the shared `ActivityTimeline`, whose `skipped` mark is a dash on a warning
 * ground precisely because this codebase would not let it be a tick. What the
 * redesign adds is the half of the apply lifecycle the SPA was not showing.
 * `GET /api/plans` and `GET /api/plans/{id}` both existed and neither was
 * called, so an apply was a spinner with no inside and a fleet's apply history
 * was invisible from the console that performs applies. Now the apply polls its
 * own stored plan and streams `progress` into a timeline, and past plans are a
 * timeline of their own beneath it. `running` is what separates "in flight"
 * from "not started" — `state === null` covers both, and only one of them means
 * there is something to wait for, so the two are never drawn the same way.
 *
 * Three ordering decisions are load-bearing. `withheld` is rendered above
 * everything, before any count, because a partial render can never be applied
 * and a headline of "3 files changed" above that fact invites someone to reason
 * about the three. Cosmetic churn moved out of the change list into its own
 * collapsed section rather than a checkbox that folds it back in, so approval
 * and churn are never in the same list. And blocking-ness is stated on every
 * gate but only *coloured* on gates that did not pass: on a green checklist it
 * is trivia, and colour here is spent only where a human is needed.
 *
 * ── the compaction pass ─────────────────────────────────────────────────────
 *
 * Six stacked full-width cards made an operator scroll past the whole change
 * list to reach the button, so from 1200px up the page is two columns: what the
 * plan *is* on the left (gates, changes, churn, validation), what you *do* with
 * it on the right (apply, history). The apply panel and the gate checklist are
 * then legible at the same time, which is the pairing the decision actually
 * needs. Below 1200px it collapses back to one column in the original order —
 * reading order and DOM order stay the same, so nothing about the sequence is
 * carried by CSS alone.
 *
 * Every descriptive subtitle that explained the console to its own operator is
 * gone. Three sentences were kept and are not prose: that a gate which could
 * not run is shown as not run and never as a pass; that plans live in memory
 * and do not survive a controller restart; and that applying re-renders and
 * refuses if anything moved. Each states a guarantee or a limit that the
 * surrounding controls do not, and deleting one would make the page mislead.
 * The `withheld` callout keeps its full width above both columns, because it is
 * the one fact that invalidates everything either column has to say.
 *
 * The KPI strip gained cosmetic churn as a tile of its own. It was previously
 * only a hint hanging off "files changed", which is the one place a number
 * excluded from the headline should not live.
 */

import { useId, useMemo, useState } from 'react';

import { Badge, type Tone } from '../components/Badge';
import { Callout } from '../components/Callout';
import { FindingList } from '../components/FindingList';
import { Freshness } from '../components/Freshness';
import { PageHeader } from '../components/PageHeader';
import { StatGrid, StatTile } from '../components/StatTile';
import { EmptyBlock, ErrorBlock, SkeletonRows, StaleBanner } from '../components/States';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader, KeyValue, KeyValueRow } from '../components/ui/Card';
import { CopyButton } from '../components/ui/CopyButton';
import { FilterBar, SegmentedFilter } from '../components/ui/FilterBar';
import { ChevronRightIcon } from '../components/ui/Icon';
import {
  ActivityTimeline,
  Stepper,
  type Step,
  type TimelineItem,
  type TimelineStatus,
} from '../components/ui/Timeline';
import { apiPost, describeError } from '../lib/api';
import {
  formatDateTime,
  formatNumber,
  formatRelative,
  isoAttribute,
  pluralise,
  UNKNOWN,
} from '../lib/format';
import { useResource, type ResourceState } from '../lib/useResource';
import type {
  ApplyResponse,
  ApplyState,
  ChangeKind,
  FileChangeResponse,
  GateStatus,
  PlanResponse,
  PreflightGate,
  StepStatus,
  StoredPlanResponse,
} from '../types/api';

import '../styles/page-plan.css';

/** How often to ask a running apply where it has got to. */
const PROGRESS_POLL_MS = 1_500;
/** History is a cheap in-memory read; slow poll keeps another operator's apply visible. */
const HISTORY_POLL_MS = 30_000;

const GATE_TONE: Record<GateStatus, Tone> = {
  pass: 'good',
  fail: 'critical',
  skipped: 'warning',
};

const GATE_LABEL: Record<GateStatus, string> = {
  pass: 'Pass',
  fail: 'Fail',
  skipped: 'Not run',
};

/**
 * `skipped` maps to the timeline's own `skipped`, never to `ok`.
 *
 * The timeline draws that as a dash on a warning ground — a different silhouette
 * as well as a different colour, so the distinction survives greyscale, CVD and
 * forced-colors. A gate that could not be evaluated is not a gate that passed.
 */
const GATE_TIMELINE: Record<GateStatus, TimelineStatus> = {
  pass: 'ok',
  fail: 'failed',
  skipped: 'skipped',
};

const STEP_TIMELINE: Record<StepStatus, TimelineStatus> = {
  ok: 'ok',
  failed: 'failed',
  skipped: 'skipped',
};

const KIND_TONE: Record<ChangeKind, Tone> = {
  added: 'good',
  modified: 'info',
  deleted: 'critical',
  unchanged: 'neutral',
};

/** Deletions read first: they are the ones nobody expects. */
const KIND_ORDER: Record<ChangeKind, number> = {
  deleted: 0,
  added: 1,
  modified: 2,
  unchanged: 3,
};

const APPLY_STATE_TONE: Record<ApplyState, Tone> = {
  applied: 'good',
  rolled_back: 'warning',
  rollback_failed: 'critical',
  refused: 'neutral',
};

const APPLY_STATE_TITLE: Record<ApplyState, string> = {
  applied: 'Applied',
  rolled_back: 'Rolled back — the fleet is as you left it',
  rollback_failed: 'ROLLBACK FAILED — the fleet needs attention now',
  refused: 'Refused — nothing was touched',
};

const APPLY_STATE_LABEL: Record<ApplyState, string> = {
  applied: 'Applied',
  rolled_back: 'Rolled back',
  rollback_failed: 'Rollback failed',
  refused: 'Refused',
};

type KindFilter = 'all' | ChangeKind;

/** Group changed files the way an operator reasons about them. */
function groupOf(change: FileChangeResponse): string {
  if (change.center_slug) return `Center ${change.center_slug}`;
  if (change.relpath.startsWith('caddy/')) return 'Routing and TLS (shared)';
  if (change.relpath.startsWith('viewer/')) return 'Viewer config (shared)';
  return 'Compose project (shared)';
}

/**
 * Classify one diff line for colouring.
 *
 * `+++`/`---` are file headers, not an insert and a delete — colouring them
 * would put a green and a red line at the top of every single diff.
 */
function diffLineKind(line: string): 'add' | 'del' | 'hunk' | 'meta' | 'ctx' {
  if (line.startsWith('+++') || line.startsWith('---')) return 'meta';
  if (line.startsWith('@@')) return 'hunk';
  if (line.startsWith('+')) return 'add';
  if (line.startsWith('-')) return 'del';
  return 'ctx';
}

/** "2 modified · 1 added", in the order deletions-first. */
function kindBreakdown(changes: readonly FileChangeResponse[]): string {
  const counts = new Map<ChangeKind, number>();
  for (const change of changes) {
    counts.set(change.kind, (counts.get(change.kind) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => KIND_ORDER[a[0]] - KIND_ORDER[b[0]])
    .map(([kind, count]) => `${count} ${kind}`)
    .join(' · ');
}

/* ── one changed file ──────────────────────────────────────────────────────── */

function FileChangeRow({ change }: { change: FileChangeResponse }) {
  const [open, setOpen] = useState(false);
  const diffId = useId();

  const lines = change.unified_diff ? change.unified_diff.split('\n') : [];
  const hasDiff = lines.length > 0;

  return (
    <li className={`plan-file${change.is_cosmetic ? ' plan-file--cosmetic' : ''}`}>
      <button
        type="button"
        className="plan-file__summary"
        aria-expanded={open}
        aria-controls={open && hasDiff ? diffId : undefined}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        disabled={!hasDiff}
      >
        <ChevronRightIcon
          size={14}
          className={`plan-file__chevron${open ? ' plan-file__chevron--open' : ''}`}
        />
        <span className="plan-file__path">{change.relpath}</span>
        <Badge tone={KIND_TONE[change.kind]} small>
          {change.kind}
        </Badge>
        {change.is_cosmetic ? (
          <Badge tone="neutral" small>
            cosmetic only
          </Badge>
        ) : null}
        {hasDiff ? null : (
          // Not "no changes" — the change is real, the server simply sent no
          // diff text for it (a new file, or one it declined to render inline).
          <span className="plan-file__note">no diff text</span>
        )}
      </button>

      {open && hasDiff ? (
        <pre
          id={diffId}
          className="code-block plan-diff"
          tabIndex={0}
          aria-label={`Diff for ${change.relpath}`}
        >
          {lines.map((line, index) => (
            <span
              key={index}
              className={`plan-diff__line plan-diff__line--${diffLineKind(line)}`}
            >
              {line || ' '}
              {'\n'}
            </span>
          ))}
        </pre>
      ) : null}
    </li>
  );
}

function FileList({ changes }: { changes: readonly FileChangeResponse[] }) {
  return (
    <ul className="plan-files">
      {changes.map((change) => (
        <FileChangeRow key={change.relpath} change={change} />
      ))}
    </ul>
  );
}

/* ── preflight ─────────────────────────────────────────────────────────────── */

function PreflightCard({ gates }: { gates: readonly PreflightGate[] }) {
  const blocking = gates.filter((gate) => gate.blocking).length;

  const items: TimelineItem[] = gates.map((gate) => ({
    id: gate.name,
    status: GATE_TIMELINE[gate.status],
    title: (
      <span className="plan-gate__head">
        <span>{gate.name}</span>
        <Badge tone={GATE_TONE[gate.status]} small>
          {GATE_LABEL[gate.status]}
        </Badge>
      </span>
    ),
    detail: gate.detail,
    // Stated on every gate; coloured only where the answer changes what
    // happens next.
    time: (
      <span
        className={`plan-gate__scope${
          gate.blocking && gate.status !== 'pass' ? ' plan-gate__scope--live' : ''
        }`}
      >
        {gate.blocking ? 'Blocks apply' : 'Advisory'}
      </span>
    ),
  }));

  return (
    <Card>
      {/* The subtitle is down to the one sentence that is a guarantee rather
          than a description. It is the page's central refusal and stays
          verbatim. */}
      <CardHeader
        title="Preflight"
        count={gates.length}
        subtitle="A gate that could not run is shown as not run — never as a pass."
      />
      <CardBody>
        {gates.length === 0 ? (
          <EmptyBlock title="No gates were reported">
            This render returned no preflight checks at all, which is itself worth
            asking about — it is not the same as every check passing.
          </EmptyBlock>
        ) : (
          <>
            <ActivityTimeline items={items} label="Preflight gates" />
            <p className="plan-gate__note">
              {blocking} of {gates.length} {pluralise(gates.length, 'gate')} would stop
              an apply; the rest are advisory.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/* ── apply history ─────────────────────────────────────────────────────────── */

function historyStatus(plan: StoredPlanResponse): TimelineStatus {
  // `running` first: `state` is null both before an apply starts and while one
  // is in flight, and those are not the same event.
  if (plan.running) return 'active';
  switch (plan.state) {
    case 'applied':
      return 'ok';
    case 'rolled_back':
      // Not `ok` and not `failed`: it ran, it did not stand, nothing is left
      // behind. The timeline's dash says "no effect", which is exactly right.
      return 'skipped';
    case 'rollback_failed':
      return 'failed';
    case 'refused':
      return 'info';
    default:
      return 'pending';
  }
}

function StateBadge({ plan }: { plan: StoredPlanResponse }) {
  if (plan.running) {
    return (
      <Badge tone="info" small>
        Applying now
      </Badge>
    );
  }
  if (plan.state === null) {
    return (
      <Badge tone="neutral" small>
        Never applied
      </Badge>
    );
  }
  return (
    <Badge tone={APPLY_STATE_TONE[plan.state]} small>
      {APPLY_STATE_LABEL[plan.state]}
    </Badge>
  );
}

function ApplyHistoryCard({ history }: { history: ResourceState<StoredPlanResponse[]> }) {
  const plans = history.data ?? [];

  const items: TimelineItem[] = plans.map((plan) => ({
    id: plan.id,
    status: historyStatus(plan),
    title: (
      <span className="plan-history__title">
        <span className="mono">{plan.id}</span>
        {plan.risk === 'high' ? (
          <Badge tone="critical" small>
            high risk
          </Badge>
        ) : null}
        <StateBadge plan={plan} />
      </span>
    ),
    time: (
      <time dateTime={isoAttribute(plan.created_at)} title={formatDateTime(plan.created_at)}>
        {formatRelative(plan.created_at)}
      </time>
    ),
    detail: (
      <>
        Created by {plan.created_by} · {formatNumber(plan.changed_paths.length)}{' '}
        {pluralise(plan.changed_paths.length, 'file')}
        {plan.changed_center_slugs.length > 0
          ? ` across ${plan.changed_center_slugs.join(', ')}`
          : ' (shared files only)'}
        {plan.applied_at ? (
          <>
            {' · applied by '}
            {plan.applied_by ?? UNKNOWN} {formatRelative(plan.applied_at)}
          </>
        ) : null}
      </>
    ),
  }));

  return (
    <Card>
      <CardHeader
        title="Apply history"
        count={history.data ? plans.length : undefined}
        subtitle="Newest first. Plans are held in memory and do not survive a controller restart."
        actions={
          <Freshness
            checkedAt={history.fetchedAt}
            refreshing={history.refreshing}
            onRefresh={history.reload}
            label="Read"
            staleAfterMs={HISTORY_POLL_MS * 3}
          />
        }
      />
      <CardBody>
        {history.loading && !history.data ? (
          <SkeletonRows rows={3} />
        ) : history.error && !history.data ? (
          <ErrorBlock what="the apply history" error={history.error} onRetry={history.reload} />
        ) : (
          <>
            {history.error ? <StaleBanner error={history.error} onRetry={history.reload} /> : null}
            {plans.length === 0 ? (
              <EmptyBlock title="No plan has been created yet">
                Nothing has been frozen for approval on this controller. Creating a plan
                writes nothing on its own — it only fixes what an apply would be checked
                against.
              </EmptyBlock>
            ) : (
              <ActivityTimeline items={items} label="Apply history" />
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

/* ── apply ─────────────────────────────────────────────────────────────────── */

/**
 * Create a plan, confirm it, apply it, and watch it run.
 *
 * The confirmation is the plan id rather than a fixed word. A fixed phrase can
 * be typed before the diff is read, or pasted from muscle memory; the id can
 * only be produced by looking at the plan in front of you. The button stays
 * disabled until it matches exactly.
 *
 * While the apply request is in flight the panel polls `GET /api/plans/{id}`
 * and renders `progress` as it arrives, so "recreating containers" is something
 * the operator watches rather than infers from a spinner. The poll stops the
 * moment the POST returns, because from then on `ApplyResponse` is the complete
 * and final account and a second source could only disagree with it.
 */
function ApplyPanel({
  blockedReason,
  onCreated,
  onApplied,
}: {
  /** Non-empty when the controller would refuse to mint a new plan right now. */
  blockedReason: string;
  onCreated: () => void;
  onApplied: () => void;
}) {
  const [stored, setStored] = useState<StoredPlanResponse | null>(null);
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApplyResponse | null>(null);
  const [showWritten, setShowWritten] = useState(false);

  const writtenId = useId();

  // Enabled only between "a plan exists" and "the apply has answered". The path
  // is a deliberate 404 while disabled, so a wiring mistake fails loudly rather
  // than quietly parsing the plan *list* as a single plan.
  const live = useResource<StoredPlanResponse>(
    stored ? `/api/plans/${stored.id}` : '/api/plans/none',
    {
      enabled: stored !== null && result === null,
      refreshMs: PROGRESS_POLL_MS,
    },
  );

  // Guarded on both the id and on `result`: a poll from a previous plan must
  // never be read as this one's progress, and the last poll before the apply
  // returned would otherwise keep reporting `running` forever after it landed.
  const watched =
    stored && !result && live.data && live.data.id === stored.id ? live.data : null;
  const running = watched?.running ?? false;
  const progress = watched?.progress ?? [];
  const inFlight = busy || running;

  async function createPlan() {
    setBusy(true);
    setError(null);
    try {
      const created = await apiPost<StoredPlanResponse>('/api/plans');
      setStored(created);
      setArmed(false);
      setResult(null);
      setShowWritten(false);
      onCreated();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  async function applyPlan() {
    if (!stored) return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await apiPost<ApplyResponse>(`/api/plans/${stored.id}/apply`);
      setResult(outcome);
      onApplied();
    } catch (cause) {
      setError(describeError(cause));
      onApplied();
    } finally {
      setBusy(false);
    }
  }


  /** The apply's own outcome, in the timeline's vocabulary. */
  function applyStepStatus(): TimelineStatus {
    if (result) {
      if (result.state === 'applied') return 'ok';
      // Refused means the guards stopped it before anything was touched: it did
      // not run, so it is `skipped` and never a tick.
      if (result.state === 'refused') return 'skipped';
      return 'failed';
    }
    return inFlight ? 'active' : 'pending';
  }

  function confirmStepStatus(): TimelineStatus {
    if (result) return 'ok';
    if (!stored) return 'pending';
    return armed ? 'ok' : 'active';
  }

  const steps: Step[] = [
    {
      id: 'freeze',
      label: 'Freeze the render',
      status: stored ? 'ok' : 'active',
      detail: stored ? `Plan ${stored.id}` : 'Writes nothing',
    },
    {
      id: 'confirm',
      label: 'Confirm the change',
      status: confirmStepStatus(),
      detail: stored && !result ? (armed ? 'Armed' : 'Not yet confirmed') : undefined,
    },
    {
      id: 'apply',
      label: 'Apply to the fleet',
      status: applyStepStatus(),
      detail: result ? APPLY_STATE_LABEL[result.state] : inFlight ? 'Running' : undefined,
    },
  ];

  const progressItems: TimelineItem[] = progress.map((step, index) => ({
    id: `${step.name}-${index}`,
    status: STEP_TIMELINE[step.status],
    title: step.name,
    detail: step.detail,
  }));
  if (running) {
    progressItems.push({
      id: 'in-flight',
      status: 'active',
      title: 'Working…',
      detail: 'The controller has not reported the next step yet.',
    });
  }

  return (
    <Card>
      <CardHeader title="Apply" />
      <CardBody>
        <div className="plan-apply">
          <Stepper steps={steps} label="Apply workflow" />

          {error ? (
            <Callout tone="critical" title="That did not go through" live>
              {error}
            </Callout>
          ) : null}

          {result ? (
            <>
              <Callout
                tone={APPLY_STATE_TONE[result.state]}
                title={APPLY_STATE_TITLE[result.state]}
                live
              >
                {result.needs_attention ? (
                  <>
                    The change failed <em>and</em> could not be undone. The files as they
                    were before this apply are in{' '}
                    <code>{result.snapshot_dir ?? 'the snapshot directory'}</code>. Do not
                    run another apply until the fleet has been checked by hand.
                  </>
                ) : result.state === 'applied' ? (
                  <>
                    {result.written.length} {pluralise(result.written.length, 'file')}{' '}
                    written, containers recreated and Caddy reloaded. Every touched center
                    was verified to answer on loopback and to challenge for credentials
                    publicly.
                  </>
                ) : (
                  <>
                    Nothing was left half-done. Snapshot:{' '}
                    <code>{result.snapshot_dir ?? 'n/a'}</code>
                  </>
                )}
              </Callout>

              {result.refusals.length > 0 ? (
                <ul className="plan-reasons">
                  {result.refusals.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              ) : null}

              <ActivityTimeline
                items={result.steps.map((step, index) => ({
                  id: `${step.name}-${index}`,
                  status: STEP_TIMELINE[step.status],
                  title: step.name,
                  detail: step.detail,
                }))}
                label="Apply steps"
              />

              {result.written.length > 0 ? (
                <div>
                  <Button
                    kind="ghost"
                    size="sm"
                    aria-expanded={showWritten}
                    aria-controls={showWritten ? writtenId : undefined}
                    onClick={() => setShowWritten((was) => !was)}
                  >
                    {showWritten ? 'Hide' : 'Show'} the {result.written.length}{' '}
                    {pluralise(result.written.length, 'file')} written
                  </Button>
                  {showWritten ? (
                    <ul className="plan-written" id={writtenId}>
                      {result.written.map((path) => (
                        <li key={path}>{path}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              <div className="toolbar">
                <Button
                  kind="tertiary"
                  onClick={createPlan}
                  disabled={busy || blockedReason !== ''}
                >
                  Create another plan
                </Button>
                {blockedReason ? <span className="muted">{blockedReason}</span> : null}
              </div>
            </>
          ) : stored === null ? (
            <>
              {/* Two sentences down to one. What a plan *is* is now carried by
                  the stepper above ("Freeze the render / Writes nothing"); what
                  survives here is the guarantee, which nothing else states. */}
              <p className="muted plan-note">
                Applying re-renders and refuses if anything changed since — what gets
                written is what you read, or nothing does.
              </p>
              <div className="toolbar">
                <Button onClick={createPlan} disabled={busy || blockedReason !== ''}>
                  {busy ? 'Creating…' : 'Create plan'}
                </Button>
                {blockedReason ? <span className="muted">{blockedReason}</span> : null}
              </div>
            </>
          ) : (
            <>
              <KeyValue>
                <KeyValueRow label="Plan">
                  <span className="mono">{stored.id}</span>
                </KeyValueRow>
                <KeyValueRow label="Created">
                  <time
                    dateTime={isoAttribute(stored.created_at)}
                    title={formatDateTime(stored.created_at)}
                  >
                    {formatRelative(stored.created_at)}
                  </time>{' '}
                  <span className="muted">by {stored.created_by}</span>
                </KeyValueRow>
                <KeyValueRow label="Risk">
                  <span className="row">
                    <Badge tone={stored.risk === 'high' ? 'critical' : 'info'} small>
                      {stored.risk}
                    </Badge>
                    <span className="muted">needs the {stored.required_role} role</span>
                  </span>
                </KeyValueRow>
                <KeyValueRow label="Touches">
                  {formatNumber(stored.changed_paths.length)}{' '}
                  {pluralise(stored.changed_paths.length, 'file')}
                  {stored.changed_center_slugs.length > 0
                    ? ` across ${stored.changed_center_slugs.join(', ')}`
                    : ' — shared files only'}
                </KeyValueRow>
                <KeyValueRow label="Digest">
                  <span className="mono" title={stored.digest}>
                    {stored.digest.slice(0, 16)}…
                  </span>
                </KeyValueRow>
              </KeyValue>

              {stored.risk_reasons.length > 0 ? (
                <Callout tone="warning" title="Why this is high risk">
                  <ul className="plan-reasons">
                    {stored.risk_reasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </Callout>
              ) : null}

              {inFlight || progressItems.length > 0 ? (
                <div className="plan-progress" aria-live="polite">
                  <p className="plan-progress__head">
                    {running ? 'Applying now' : 'Steps so far'}
                    {watched?.started_at ? (
                      <span className="plan-progress__since">
                        started {formatRelative(watched.started_at)}
                      </span>
                    ) : null}
                  </p>
                  {progressItems.length > 0 ? (
                    <ActivityTimeline items={progressItems} label="Apply progress" />
                  ) : (
                    <p className="muted">
                      The apply has been accepted. No step has reported yet.
                    </p>
                  )}
                </div>
              ) : null}

              {/* Two clicks, nothing typed. The armed state is where the blast
                  radius is stated — it is the last thing read before the fleet
                  is written to, and the only place container recreation is
                  named. Same idle → armed shape as rotating a password and
                  removing a center, so the gesture is learned once. */}
              {armed ? (
                <Callout tone="warning" title="Apply this plan to the live fleet?">
                  <p>
                    {formatNumber(stored.changed_paths.length)}{' '}
                    {pluralise(stored.changed_paths.length, 'file')} will be rewritten
                    {stored.changed_center_slugs.length > 0
                      ? ` across ${stored.changed_center_slugs.join(', ')}`
                      : ' — shared files only'}
                    . Caddy reloads and the affected containers are recreated.
                  </p>
                </Callout>
              ) : null}

              <div className="toolbar">
                {armed ? (
                  <Button kind="danger" onClick={applyPlan} disabled={inFlight}>
                    {inFlight ? 'Applying…' : 'Yes, apply to the fleet'}
                  </Button>
                ) : (
                  <Button kind="primary" onClick={() => setArmed(true)} disabled={inFlight}>
                    Apply to the fleet
                  </Button>
                )}
                <Button
                  kind="tertiary"
                  onClick={() => {
                    if (armed) {
                      setArmed(false);
                      return;
                    }
                    setStored(null);
                  }}
                  disabled={inFlight}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/* ── the page ──────────────────────────────────────────────────────────────── */

function headerStatus(data: PlanResponse, realCount: number): JSX.Element {
  if (data.withheld.length > 0) {
    return <Badge tone="critical">Partial render — cannot be applied</Badge>;
  }
  if (data.is_noop) return <Badge tone="good">Nothing to do</Badge>;
  if (!data.has_real_changes) return <Badge tone="neutral">Cosmetic churn only</Badge>;
  if (data.can_apply) {
    return (
      <Badge tone="info">
        {realCount} {pluralise(realCount, 'file')} ready to apply
      </Badge>
    );
  }
  return <Badge tone="warning">Changes pending — apply blocked</Badge>;
}

export function PlanPage() {
  const plan = useResource<PlanResponse>('/api/plan');
  const history = useResource<StoredPlanResponse[]>('/api/plans', { refreshMs: HISTORY_POLL_MS });

  const [showCosmetic, setShowCosmetic] = useState(false);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [applyAnswered, setApplyAnswered] = useState(false);

  const data = plan.data;

  const { real, cosmetic } = useMemo(() => {
    const changes = data?.changes ?? [];
    return {
      real: changes.filter((change) => !change.is_cosmetic),
      cosmetic: changes.filter((change) => change.is_cosmetic),
    };
  }, [data]);

  const query = search.trim().toLowerCase();
  const matches = useMemo(() => {
    return (change: FileChangeResponse): boolean => {
      if (kind !== 'all' && change.kind !== kind) return false;
      if (!query) return true;
      return (
        change.relpath.toLowerCase().includes(query) ||
        (change.center_slug ?? '').toLowerCase().includes(query)
      );
    };
  }, [kind, query]);

  const visibleReal = useMemo(() => real.filter(matches), [real, matches]);
  const visibleCosmetic = useMemo(() => cosmetic.filter(matches), [cosmetic, matches]);

  const grouped = useMemo(() => {
    const groups = new Map<string, FileChangeResponse[]>();
    for (const change of visibleReal) {
      const key = groupOf(change);
      const bucket = groups.get(key);
      if (bucket) bucket.push(change);
      else groups.set(key, [change]);
    }
    for (const bucket of groups.values()) {
      // Within a group, by kind then path — deletions first, because they are
      // the ones nobody expects to see.
      bucket.sort(
        (a, b) =>
          KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
          a.relpath.localeCompare(b.relpath, undefined, { numeric: true }),
      );
    }
    return [...groups.entries()].sort((a, b) => {
      // Shared files first: they affect every clinic, not one.
      const aShared = a[0].includes('shared');
      const bShared = b[0].includes('shared');
      if (aShared !== bShared) return aShared ? -1 : 1;
      return a[0].localeCompare(b[0], undefined, { numeric: true });
    });
  }, [visibleReal]);

  const gates = data?.gates ?? [];
  const failed = gates.filter((gate) => gate.status === 'fail');
  const skipped = gates.filter((gate) => gate.status === 'skipped');
  const blockingFindings = (data?.findings ?? []).filter((f) => f.severity === 'block');

  const kindCounts = useMemo(() => {
    const counts = new Map<ChangeKind, number>();
    for (const change of real) counts.set(change.kind, (counts.get(change.kind) ?? 0) + 1);
    return counts;
  }, [real]);

  function reloadAll() {
    plan.reload();
    history.reload();
  }

  return (
    <>
      <PageHeader
        title="Plan"
        status={data ? headerStatus(data, real.length) : null}
        actions={
          <Freshness
            checkedAt={data?.rendered_at ?? null}
            onRefresh={reloadAll}
            refreshing={plan.refreshing}
            label="Rendered"
          />
        }
      />

      {plan.error && !data ? (
        <ErrorBlock error={plan.error} onRetry={plan.reload} what="the plan" />
      ) : null}
      {plan.error && data ? <StaleBanner error={plan.error} onRetry={plan.reload} /> : null}
      {plan.loading && !data ? <SkeletonRows rows={4} /> : null}

      {data ? (
        <>
          {/* Keyed off `withheld`, not off blocking findings generally. The two
              are different claims, and conflating them produced a page that
              announced missing basic-auth material directly above a preflight
              gate reporting all 26 centers' auth as fine. A malformed peer
              blocks rendering without withholding anything.

              Rendered before the counts, not after: a headline of "3 files
              changed" above the fact that the render is incomplete invites
              someone to reason about the three. */}
          {data.withheld.length > 0 ? (
            <Callout
              tone="critical"
              live
              title={`${data.withheld.length} generated ${pluralise(data.withheld.length, 'file')} could not be rendered — this plan can never be applied`}
            >
              <p>
                <code>{data.withheld.join(', ')}</code>{' '}
                {pluralise(data.withheld.length, 'was', 'were')} withheld rather than
                written incorrectly. A center with no basic-auth material renders an empty{' '}
                <code>basic_auth</code> hash, which is an invalid Caddyfile — and because
                one Caddy container fronts the whole fleet, that removes the only access
                control in front of every clinic, not just the one that is misconfigured.
              </p>
              <p>
                This is <strong>not</strong> a proposal to delete{' '}
                {pluralise(data.withheld.length, 'that file', 'those files')}. The fleet
                still needs {pluralise(data.withheld.length, 'it', 'them')}; this plan
                simply cannot say what {pluralise(data.withheld.length, 'it', 'they')}{' '}
                should contain, and so cannot be applied at all.
              </p>
            </Callout>
          ) : null}

          {blockingFindings.length > 0 ? (
            <Callout
              tone="critical"
              title={`${blockingFindings.length} blocking ${pluralise(blockingFindings.length, 'finding')} — this fleet cannot be applied`}
            >
              Rendering refuses while any of these stand. See Validation below for what
              each one is and how to clear it.
            </Callout>
          ) : null}

          {!data.can_apply ? (
            // The sentence, not just an absent button: "not built", "not
            // permitted" and "not safe right now" are indistinguishable
            // otherwise.
            <Callout tone="info" title="Applying is not available">
              {data.apply_blocked_reason}
            </Callout>
          ) : null}

          <StatGrid>
            <StatTile
              label="Files changed"
              value={formatNumber(real.length)}
              hint="cosmetic churn excluded"
            />
            <StatTile
              label="Centers affected"
              value={formatNumber(data.changed_center_slugs.length)}
              hint={
                data.changed_center_slugs.length === 0
                  ? 'shared files only'
                  : data.changed_center_slugs.slice(0, 3).join(', ') +
                    (data.changed_center_slugs.length > 3
                      ? ` +${data.changed_center_slugs.length - 3} more`
                      : '')
              }
            />
            <StatTile
              label="Gates failed"
              value={formatNumber(failed.length)}
              tone={failed.length > 0 ? 'critical' : undefined}
              hint={failed.length > 0 ? 'a check answered no' : 'every check that ran passed'}
            />
            <StatTile
              label="Gates not run"
              value={formatNumber(skipped.length)}
              tone={skipped.length > 0 ? 'warning' : undefined}
              hint={skipped.length > 0 ? 'unknown — not passed' : 'every gate was evaluated'}
            />
            {/* Promoted out of the "files changed" hint. A number deliberately
                excluded from the headline needs to be visible as its own
                number, or the exclusion looks like an omission. */}
            <StatTile
              label="Cosmetic churn"
              value={formatNumber(cosmetic.length)}
              hint={cosmetic.length > 0 ? 'changes nothing the fleet does' : 'none'}
            />
            {data.withheld.length > 0 ? (
              <StatTile
                label="Files withheld"
                value={formatNumber(data.withheld.length)}
                tone="critical"
                hint="the render is incomplete"
              />
            ) : null}
          </StatGrid>

          {/* Two columns from 1200px, one below it. The DOM order is the
              single-column reading order, so the sequence never depends on the
              grid being applied. */}
          <div className="plan-cols">
            <div className="plan-cols__main">
              <PreflightCard gates={gates} />

              <Card>
                <CardHeader title="Changes" count={real.length} />
                <CardBody>
                  {data.is_noop ? (
                    <EmptyBlock title="Nothing to do">
                      The generated files on disk already match what this fleet renders to.
                    </EmptyBlock>
                  ) : real.length === 0 ? (
                    <EmptyBlock title="No substantive changes">
                      Every difference found is cosmetic. Applying would change nothing the
                      fleet does.
                    </EmptyBlock>
                  ) : (
                    <>
                      <FilterBar
                        search={search}
                        onSearch={setSearch}
                        searchPlaceholder="Filter by path or center slug…"
                        searchLabel="Filter changed files"
                        count={
                          visibleReal.length === real.length
                            ? `${formatNumber(real.length)} ${pluralise(real.length, 'file')}`
                            : `${formatNumber(visibleReal.length)} of ${formatNumber(real.length)}`
                        }
                      >
                        <SegmentedFilter<KindFilter>
                          legend="Filter by change kind"
                          value={kind}
                          onChange={setKind}
                          options={[
                            { value: 'all', label: 'All', count: real.length },
                            { value: 'deleted', label: 'Deleted', count: kindCounts.get('deleted') ?? 0 },
                            { value: 'added', label: 'Added', count: kindCounts.get('added') ?? 0 },
                            { value: 'modified', label: 'Modified', count: kindCounts.get('modified') ?? 0 },
                          ]}
                        />
                      </FilterBar>

                      {grouped.length === 0 ? (
                        <EmptyBlock title="Nothing matches that filter">
                          {query ? (
                            <>
                              No changed file matches <strong>{search}</strong>
                              {kind !== 'all' ? ` among ${kind} files` : ''}.
                            </>
                          ) : (
                            `Nothing in this plan is ${kind}.`
                          )}
                        </EmptyBlock>
                      ) : (
                        <div className="plan-groups">
                          {grouped.map(([group, changes]) => (
                            <div key={group}>
                              <div className="plan-group__head">
                                <h3 className="plan-group__title">{group}</h3>
                                <span className="plan-group__breakdown">
                                  {kindBreakdown(changes)}
                                </span>
                              </div>
                              <FileList changes={changes} />
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>

              {/* Its own section rather than a checkbox that folds churn back into
                  the approval list. Raw bytes differ, normalised content does not;
                  nobody should be asked to approve a re-salted hash. */}
              {cosmetic.length > 0 ? (
                <Card>
                  <CardHeader
                    title="Cosmetic churn"
                    count={cosmetic.length}
                    subtitle="Raw bytes differ, normalised content does not — excluded from the count above."
                    actions={
                      <Button
                        kind="ghost"
                        size="sm"
                        aria-expanded={showCosmetic}
                        onClick={() => setShowCosmetic((was) => !was)}
                      >
                        {showCosmetic ? 'Hide' : 'Inspect'} {cosmetic.length}{' '}
                        {pluralise(cosmetic.length, 'file')}
                      </Button>
                    }
                  />
                  {showCosmetic ? (
                    <CardBody>
                      {visibleCosmetic.length === 0 ? (
                        <p className="muted">No cosmetic change matches the filter above.</p>
                      ) : (
                        <FileList changes={visibleCosmetic} />
                      )}
                    </CardBody>
                  ) : null}
                </Card>
              ) : null}

              {data.findings.length > 0 ? (
                <Card>
                  <CardHeader title="Validation" count={data.findings.length} />
                  <CardBody>
                    <FindingList findings={data.findings} label="Plan findings" />
                  </CardBody>
                </Card>
              ) : null}
            </div>

            <div className="plan-cols__side">
              {/* Stays mounted once an apply has answered, even though a successful
                  one makes the next render a no-op and flips `can_apply` to false.
                  Unmounting there would delete the step log and the snapshot path
                  at the exact moment they are worth reading — and if a rollback
                  failed, that log is the incident. The create button is disabled
                  with the controller's own sentence instead. */}
              {data.can_apply || applyAnswered ? (
                <ApplyPanel
                  blockedReason={data.can_apply ? '' : data.apply_blocked_reason}
                  onCreated={history.reload}
                  onApplied={() => {
                    setApplyAnswered(true);
                    reloadAll();
                  }}
                />
              ) : null}

              <ApplyHistoryCard history={history} />
            </div>
          </div>

          {/* A value an operator compares between renders by eye, so it is
              worth being able to take it away whole rather than off a
              truncated display. */}
          <Card flush>
            <p className="plan-digest">
              <span>Plan digest</span>
              <code title={data.digest}>{data.digest.slice(0, 16)}…</code>
              <CopyButton text={data.digest} label="Copy digest" />
              <span>
                — a re-render must produce the same value, or the fleet moved underneath
                this preview.
              </span>
            </p>
          </Card>
        </>
      ) : null}
    </>
  );
}
